import { GetUsersByIdsGQL, UpdateUserSettingsGQL } from './../../generated/queries';
import { Injectable } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { FilterParser } from '@core/filter-parser';
import { PageFilter } from '@core/model/pagination/filter';
import { PageResult } from '@core/model/pagination/page-result';
import { UpdateUserSettings, User } from '@core/model/user/user';
import { UserAgentConfig, Role, AssignUserRoles, CreateUser, CreateProjectClient, ProjectClientOwner, UpdateUser } from '@core/model/user/worker';
import { LocalStorageDataService } from '@core/services/local-storage.service';
import { ObjectUtils, strictMap } from '@core/utils/object-utils';
import { GetPaginatedUsersQuery, GetPaginatedUsersDocument, GetUserQuery, GetUserDocument, GetUserRolesQuery, GetUserRolesDocument, AssignRolesToUserMutation, AssignRolesToUserDocument, CreateClientOwnerUserMutation, CreateClientOwnerUserDocument, CreateClientOwnersMutation, CreateClientOwnersDocument, SyncActiveDirectoryUsersMutation, SyncActiveDirectoryUsersDocument, SyncSingleUserMutation, SyncSingleUserDocument, ArchiveUserMutation, ArchiveUserDocument, RestoreUserMutation, RestoreUserDocument, CreateUserGolfDocument, UpdateUserGolfDocument, UpdateUserGolfMutation, CreateUserGolfMutation} from '@generated/auth';
import { BusinessLookup, UserFilterInput, DisableUserAgentConfigGQL, UpdateUserAgentConfigGQL, GetUserAgentConfigGQL, GetActiveUsersGQL, GetDropdownUsersGQL, GetUserDetailGQL, CreateBusinessUserWithoutRoleGQL, GetUsersWithYearlyQuotaGQL, GetMyYearlyQuotasGQL, GetYearlyQuotasGQL, AddYearlyQuotaGQL, UpdateYearlyQuotaGQL, GetQuotaLookupsGQL, GetQuotaLookupsByParentCodeGQL, GetQuotaLookupGQL, GetAllActiveUsersGQL, GetAllActiveUsersWithFilterGQL, States, CreateYearlyQuotaInput, UpdateYearlyQuotaInput } from '@generated/queries';
import { ApolloBase, Apollo } from 'apollo-angular';
import { plainToClass, plainToInstance } from 'class-transformer';
import { Observable, firstValueFrom, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  users$: Observable<PageResult<User>>;
  siteUsers$: Observable<PageResult<User>>;
  quotaTypes$: Observable<PageResult<BusinessLookup>>;
  pageFilter: PageFilter = {
    pageIndex: 0,
    activeIndex: 1,
    take: 20,
    search: '',
    state: '',
  };
  private apollo: ApolloBase;
  vmFilters: UserFilterInput = {
    or: [
      {
        firstName: { contains: '' },
      },
      {
        lastName: { contains: '' },
      },
      {
        email: { contains: '' },
      },
    ],
  };
  constructor(
    private disableWorkerAgentConfigService: DisableUserAgentConfigGQL,
    private updateWorkerAgentConfigService: UpdateUserAgentConfigGQL,
    private userAgentConfiguration: GetUserAgentConfigGQL,
    private usersQuery: GetActiveUsersGQL,
    private dropdownUsers: GetDropdownUsersGQL,
    private apolloProvider: Apollo,
    private localStorageService: LocalStorageDataService,
    private userDetail: GetUserDetailGQL,
    private usersDetailById:GetUsersByIdsGQL,
    private userWithoutRole: CreateBusinessUserWithoutRoleGQL,
    private usersWithYearlyQuotas: GetUsersWithYearlyQuotaGQL,
    private myYearlyQuotas: GetMyYearlyQuotasGQL,
    private userYearlyQuota: GetYearlyQuotasGQL,
    private addUserYearlyQuota: AddYearlyQuotaGQL,
    private updateUserYearlyQuota: UpdateYearlyQuotaGQL,
    private quotaLookups: GetQuotaLookupsGQL,
    private quotaLookupsByParentCode: GetQuotaLookupsByParentCodeGQL,
    private quotaLookup: GetQuotaLookupGQL,
    private activeUsers:GetAllActiveUsersGQL,
    private activeUsersWithFilter:GetAllActiveUsersWithFilterGQL,
    private updateuserSetting:UpdateUserSettingsGQL
  ) {
    this.apollo = this.apolloProvider.use('authClient');
    this.fetchUsers$(this.pageFilter,false);
  }

  getActivePaginatedUsers(
    filter: PageFilter,
     departmentId?:string,
    emails?: string[],
    ids?: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fetchPolicy?: any
  ): Observable<PageResult<User>> {
    this.vmFilters = {
      or: [
        {
          firstName: {
            contains: filter.search?.trim() ?? '',
          },
        },
        {
          lastName: {
            contains: filter.search?.trim() ?? '',
          },
        },
        {
          email: {
            contains: filter.search?.trim() ?? '',
          },
        },
      ],
    };
    const searchParts = filter.search?.trim().split(/\s+/).filter(Boolean) ?? [];
    if (searchParts.length > 1) {
      const lastName = searchParts[searchParts.length - 1];
      const firstName = searchParts.slice(0, -1).join(' ');
      this.vmFilters = {
        and: [
          {
            firstName: {
              contains: firstName,
            },
          },
          {
            lastName: {
              contains: lastName,
            },
          },
        ],
      };
    }
    if (departmentId) {
      const deptCondition = { departmentId: { eq: departmentId } };
      if (this.vmFilters.and) {
        this.vmFilters.and.push(deptCondition);
      } else {
        this.vmFilters.and = [deptCondition];
      }
    }

    if (emails?.length)
      this.vmFilters.and = [
        {
          email: {
            in: emails,
          },
        },
      ];
    if (ids?.length) {
  if (this.vmFilters.and?.length) {
    this.vmFilters.and.push({
      id: { in: ids },
    });
  } else {
    this.vmFilters.and = [
      {
        id: { in: ids },
      },
    ];
  }
}
    const paginationFilter = FilterParser.parsePageFilter(filter);
    return this.usersQuery
      .fetch(
        {
          filter: this.vmFilters,
          skip: paginationFilter.skip,
          take: paginationFilter.take,
        },
        {
          fetchPolicy:
            fetchPolicy ?? this.localStorageService.getFetchPolicy('user'),
        }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<User>(x.data.paginatedUsers as any)));
  }

  getActiveUsers(): Promise<User[]> {

    return firstValueFrom(this.activeUsers
      .fetch(
        {

        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('employee')
        }
      )
      .pipe(map((x) => plainToInstance(User, x.data.users))));
  }

  getPaginatedUsers(filter: PageFilter): Observable<PageResult<Worker>> {
    this.vmFilters = {
      or: [
        {
          firstName: {
            contains: filter.search.trim(),
          },
        },
        {
          displayName: {
            contains: filter.search.trim(),
          },
        },
        {
          lastName: {
            contains: filter.search.trim(),
          },
        },
        {
          email: {
            contains: filter.search.trim(),
          },
        },
      ],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;

    const paginationFilter = FilterParser.parsePageFilter(filter);
    return this.apollo
      .query<GetPaginatedUsersQuery>({
        query: GetPaginatedUsersDocument,
        variables: {
          filter: this.vmFilters,
          skip: paginationFilter.skip,
          take: paginationFilter.take,
          states: ObjectUtils.isValueNull(filter.state) ? [] : filter.state,
        },
        fetchPolicy: this.localStorageService.getFetchPolicy('user'),
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<Worker>(x.data.users.paginated as any)));
  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUser(userId: string, fetchPolicy?: any): Observable<User> {
    return this.apollo
      .query<GetUserQuery>({
        query: GetUserDocument,
        variables: {
          userId,
        },
        fetchPolicy:
          fetchPolicy ?? this.localStorageService.getFetchPolicy('user'),
      })
      .pipe(map((x) => strictMap(User, x.data.users.first)));
  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUserDetail(id: string, fetchPolicy?: any): Observable<User> {
    return this.userDetail
      .fetch(
        { id },
        {
          fetchPolicy:
            fetchPolicy ?? this.localStorageService.getFetchPolicy('user'),
        }
      )
      .pipe(map((x) => strictMap(User, x.data.user)));
  }

  getUserDetailByIds(id: string[], fetchPolicy?: any): Observable<User[]> {
    return this.usersDetailById
      .fetch(
        { userIds: id },
        {
          fetchPolicy:
            fetchPolicy ?? this.localStorageService.getFetchPolicy('user'),
        }
      )
      .pipe(map((x) => strictMap(User, x.data.users)));
  }

  getUserAgentConfig(id: string): Promise<UserAgentConfig[]> {
    return firstValueFrom(this.userAgentConfiguration
      .fetch(
        { id },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('user'),
        }
      )
      .pipe(
        map((x) =>
          plainToInstance(UserAgentConfig, x.data?.user.userAgentConfigurations)
        )
      ))
  }

  getUserRoles(userId: string): Observable<Role[]> {
    return this.apollo
      .query<GetUserRolesQuery>({
        query: GetUserRolesDocument,
        variables: {
          userId,
        },
        fetchPolicy: this.localStorageService.getFetchPolicy('user'),
      })
      .pipe(map((x) => x.data.users.userRoles));
  }

  async assignUserRoles(model: AssignUserRoles) {
    return firstValueFrom(this.apollo
      .mutate<AssignRolesToUserMutation>({
        mutation: AssignRolesToUserDocument,
        variables: { model: strictMap(AssignUserRoles, model) },
        update: (cache) => ObjectUtils.clearCache(cache, 'user'),
      }));
  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createUser(model: any): Promise<string> {
    return firstValueFrom(this.apollo
      .mutate<CreateUserGolfMutation>({
        mutation: CreateUserGolfDocument,
        variables: { model: strictMap(CreateUser, model) },
        update: (cache) => ObjectUtils.clearCache(cache, 'user'),
      })
      .pipe(map((x) => x.data.users.addGolf)));
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createProjectClientOwnerUser(model: any): Promise<string> {
    const data = strictMap(CreateProjectClient, model);
    return firstValueFrom(this.apollo
      .mutate<CreateClientOwnerUserMutation>({
        mutation: CreateClientOwnerUserDocument,
        variables: { model: data },
        update: (cache) => ObjectUtils.clearCache(cache, 'user'),
      })
      .pipe(map((x) => x.data.users.createProjectClientUser)));
  }
  async createProjectClientOwners(model: ProjectClientOwner): Promise<string> {
    const data = strictMap(ProjectClientOwner, model);
    return firstValueFrom(this.apollo
      .mutate<CreateClientOwnersMutation>({
        mutation: CreateClientOwnersDocument,
        variables: { model: data },
        update: (cache) => ObjectUtils.clearCache(cache, 'client'),
      })
      .pipe(map((x) => x.data.users.addRemoveProjectClientOwners)));
  }

  async createUserWithoutRoles(model: CreateUser): Promise<string> {
    return firstValueFrom(this.userWithoutRole
      .mutate(
        { model },
        { update: (cache) => ObjectUtils.clearCache(cache, 'user') }
      )
      .pipe(map((x) => x.data.user.createUser)));
  }

  async updateUser(model: UpdateUser) {
    return firstValueFrom(this.apollo
      .mutate<UpdateUserGolfMutation>({
        mutation: UpdateUserGolfDocument,
        variables: { model: strictMap(UpdateUser, model) },
        update: (cache) => ObjectUtils.clearCache(cache, 'user'),
      }));
  }

  async sync() {
    return firstValueFrom(this.apollo
      .mutate<SyncActiveDirectoryUsersMutation>({
        mutation: SyncActiveDirectoryUsersDocument,
      }))
  }
  async syncSingleUser(userId: string) {
    return firstValueFrom(this.apollo
      .mutate<SyncSingleUserMutation>({
        mutation: SyncSingleUserDocument,
        variables: { userId: userId }
      }));
  }

  async updateUserAgentConfig(userId: string, config: UserAgentConfig) {
    delete config.id;
    return firstValueFrom(this.updateWorkerAgentConfigService
      .mutate(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { userId: userId, model: config as any },
        { update: (cache) => ObjectUtils.clearCache(cache, 'agent') }
      )
      .pipe(map((x) => x.data.trackingAgent.userAgent.updateUserAgent)));
  }

  async disableUserAgentConfig(userId: string) {
    return firstValueFrom(this.disableWorkerAgentConfigService
      .mutate(
        { userId: userId },
        { update: (cache) => ObjectUtils.clearCache(cache, 'agent') }
      )
      .pipe(map((x) => x.data.trackingAgent.userAgent.disableUserAgent)));
  }

  async archive(userId: string) {
    return firstValueFrom(this.apollo
      .mutate<ArchiveUserMutation>({
        mutation: ArchiveUserDocument,
        variables: { userId },
        update: (cache) => ObjectUtils.clearCache(cache, 'user'),
      }))
  }

  async restore(userId: string) {
    return firstValueFrom(this.apollo
      .mutate<RestoreUserMutation>({
        mutation: RestoreUserDocument,
        variables: { userId },
        update: (cache) => ObjectUtils.clearCache(cache, 'user'),
      }));
  }

  getUsers$(filter: PageFilter, event?: boolean) {
    if (event) {
      filter.search = '';
      filter.skip = 0;
    }
    const vmFilters: UserFilterInput = {
      or: [
        { firstName: { contains: filter.search.trim() } },
        { lastName: { contains: filter.search.trim() } },
        { email: { contains: filter.search.trim() } },
      ],
    };

    return this.dropdownUsers
      .fetch(
        {
          filter: vmFilters,
          skip: filter.skip,
          take: filter.take,
        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('user'),
        }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<Worker>(x.data.paginatedUsers as any)));
  }
  getUser$(id: string) {
    return this.dropdownUsers
      .fetch(
        {
          filter: { id: { eq: id } },
        },
        { fetchPolicy: this.localStorageService.getFetchPolicy('user') }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<Worker>(x.data.paginatedUsers as any)));
  }


  fetchUsers$(filter: PageFilter, event?: boolean,policy?:FetchPolicy) {
    if (event) {
      filter.search = '';
      filter.skip = 0;
    }
    const searchStr = (filter?.search != null && typeof filter.search === 'string') ? filter.search : '';
    let firstName = searchStr;
    let lastName = searchStr;
    const fullName = searchStr.split(' ').filter(Boolean);
    if (fullName.length > 1) {
      firstName = fullName[0];
      lastName = fullName[1].length <= 2 ? fullName[0] : fullName[1];
    }
    const safeTrim = (s: string) => (s ?? '').trim();
    this.vmFilters = {
  or: [
    { firstName: { contains: safeTrim(firstName) } },
    { lastName: { contains: safeTrim(lastName) } },
    { email: { contains: safeTrim(searchStr) }},
  ],
  and: [
    {
      state: { eq: States.Active }
    }
  ]
};
    this.users$ = this.dropdownUsers
      .fetch(
        {
          filter: this.vmFilters,
          skip: filter.skip,
          take: filter.take,
        },
        {
          fetchPolicy: policy? policy:  this.localStorageService.getFetchPolicy('user'),
        }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<User>(x.data.paginatedUsers as any)));
      this.siteUsers$ = this.users$;
      this.siteUsers$.subscribe(dt => {
        // console.log('this.siteUsers$',dt);
      })
  }


  fetchActiveUsersAll$(filter: PageFilter, event?: boolean) {
    if (event) {
      filter.search = '';
      filter.skip = 0;
    }
    let firstName = filter.search;
    let lastName = filter.search;
    let fullName = [];
    if (filter.search.split(' ').length > 1) {
      fullName = filter.search.split(' ');
      firstName = fullName[0];
      if(fullName[1].length<=2){
        lastName = fullName[0];
      }
      else {
        lastName = fullName[1];
      }

    }
    this.vmFilters = {
      or: [
        { firstName: { contains: firstName.trim() } },
        { lastName: { contains: lastName.trim() } },
        { email: { contains: filter.search.trim() }},
      ]
    };
    this.users$ = this.activeUsersWithFilter
      .fetch(
        {
          filter: this.vmFilters

        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('user'),
        }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<User>(x.data.users as any)));
      this.siteUsers$ = this.users$;
  }


  updateuserSettings(model: UpdateUserSettings): Promise<string> {
      return firstValueFrom(this.updateuserSetting
        .mutate({ model: strictMap(UpdateUserSettings, model) })
        .pipe(map((x) => x.data.user.updateUserSettings))
        );
    }

  setUser(id: string) {
    this.users$ = this.dropdownUsers
      .fetch(
        {
          filter: { id: { eq: id } },
        },
        { fetchPolicy: 'network-only'},
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<User>(x.data.paginatedUsers as any)));
      this.siteUsers$ = this.users$;

  }
  fetchUsersWithIds$(filter: PageFilter, event?: boolean, ids?: string[]) {
    if (event) {
      filter.search = '';
      filter.skip = 0;
    }
    let vmFilters: UserFilterInput = {
      or: [
        { firstName: { contains: filter.search.trim() } },
        { lastName: { contains: filter.search.trim() } },
        { email: { contains: filter.search.trim() } },
      ],
    };
    if (ids.length) {
      vmFilters = {
        or: [
          { firstName: { contains: filter.search.trim() } },
          { lastName: { contains: filter.search.trim() } },
          { email: { contains: filter.search.trim() } },
        ],
        and: [{ id: { in: ids } }],
      };
    }
    this.users$ = this.dropdownUsers
      .fetch(
        {
          filter: vmFilters,
          skip: filter.skip,
          take: filter.take,
        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('user'),
        }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<User>(x.data.paginatedUsers as any)));
  }
  fetchUsersWithEmails$(
    filter: PageFilter,
    event?: boolean,
    emails?: string[]
  ) {
    if (event) {
      filter.search = '';
      filter.skip = 0;
    }
    let vmFilters: UserFilterInput = {
      or: [
        { firstName: { contains: filter.search.trim() } },
        { lastName: { contains: filter.search.trim() } },
        { email: { contains: filter.search.trim() } },
      ],
    };
    if (emails.length) {
      vmFilters = {
        or: [
          { firstName: { contains: filter.search.trim() } },
          { lastName: { contains: filter.search.trim() } },
          { email: { contains: filter.search.trim() } },
        ],
        and: [{ email: { in: emails } }],
      };
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);
    this.users$ = this.dropdownUsers
      .fetch(
        {
          filter: vmFilters,
          skip: paginationFilter.skip,
          take: paginationFilter.take,
        },
        {
          fetchPolicy: this.localStorageService.getFetchPolicy('user'),
        }
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .pipe(map((x) => new PageResult<User>(x.data.paginatedUsers as any)));
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchNextUsers$(pageFilter: PageFilter, event: any) {
    pageFilter.skip = event;
    this.fetchUsers$(pageFilter);
  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchInUsers$(pageFilter: PageFilter, event: any) {
    // event can be the term string (e.g. from app-select-avatar) or an object with .term
    const term = typeof event === 'string' ? event : (event?.term != null && typeof event.term === 'string' ? event.term : '');
    pageFilter.search = term;
    pageFilter.skip = 0;
    this.fetchUsers$(pageFilter);
  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchNextUsersAll$(pageFilter: PageFilter, event: any) {
    pageFilter.skip = event;
    this.fetchActiveUsersAll$(pageFilter);
  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchInUsersAll$(pageFilter: PageFilter, event: any) {
    pageFilter.search = event.term;
    pageFilter.skip = 0;
    this.fetchActiveUsersAll$(pageFilter);
  }

  getPaginatedUsersWithYearlyQuotas(
    filter: PageFilter,
    leaveTypeId: string,
    userIds?: string[],
    fetchPolicyOverride?: FetchPolicy
  ): Promise<PageResult<User>> {
    const search = filter.search.toLowerCase().trim().split(' ');
    const orFilter = [];
    search.forEach((x) => {
      orFilter.push(
        {
          firstName: {
            contains: x,
          },
        },
        {
          lastName: {
            contains: x,
          },
        },
        {
          email: {
            contains: x,
          },
        }
      );
    });
    this.vmFilters = {
      or: orFilter,
      and: [
        {
          state: {
            nin: [States.Inactive, States.Terminated, States.Resigned]
          },
        },
      ],
    };
    const filters = FilterParser.parseFilter({
      state: filter.state,
      leaveTypeId,
    });
    if (!ObjectUtils.isObjEmpty(filters)) {
      if (filters.state && filters.leaveTypeId) {
        this.vmFilters.and.push(
          {
            leaves: {
              some: {
                state: filters.state,
              },
            },
          },
          {
            leaves: {
              all: {
                leaveTypeId: filters.leaveTypeId,
              },
            },
          }
        );
      } else if (filters.state && !filters.leaveTypeId) {
        this.vmFilters.and.push({
          leaves: {
            some: {
              state: filters.state,
            },
          },
        });
      } else {
        this.vmFilters.and.push({
          leaves: {
            some: {
              leaveTypeId: filters.leaveTypeId,
            },
          },
        });
      }
    }
    if (userIds?.length) {
      this.vmFilters.and.push({
        id: {
          in: userIds,
        },
      });
    }
    const paginationFilter = FilterParser.parsePageFilter(filter);
    return firstValueFrom(this.usersWithYearlyQuotas
      .fetch(
        {
          filter: this.vmFilters,
          skip: paginationFilter.skip,
          take: paginationFilter.take,
        },
        {
          fetchPolicy:
            fetchPolicyOverride ??
            this.localStorageService.getFetchPolicy('leave'),
        }
      )
      .pipe(
        // tap(() => {
        //   this.localStorageService.addToSystemObjects('leave');
        // }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((res) => new PageResult<User>(res?.data?.paginatedUsers as any))))
  }

  getMyYearlyQuotas() {
    return firstValueFrom(this.myYearlyQuotas
      .fetch(
        {},
        { fetchPolicy: this.localStorageService.getFetchPolicy('year') }
      )
      .pipe(map((x) => x.data.hr.session.myYearlyQuotas)))
  }
  async getYearlyQuotas(userId: string) {
    return firstValueFrom(this.userYearlyQuota
      .fetch(
        {userId},
        { fetchPolicy: this.localStorageService.getFetchPolicy('year') }
      )
      .pipe(map((x) => x.data.hr.yearlyQuotas)))
  }

  setQuotaType(typeId: string) {
    this.quotaTypes$ = this.quotaLookup
      .fetch(
        {
          id: typeId,
        },
        {
        }
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((x) => new PageResult<BusinessLookup>(x.data.paginatedBusinessLookups as any))
      );
  }

  fetchQuotaTypes(event?: boolean): void {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
    this.quotaTypes$ = this.quotaLookups
      .fetch(
        {
          search: this.pageFilter.search,
          skip: this.pageFilter.skip,
          take: this.pageFilter.take,
        },
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((x) => new PageResult<BusinessLookup>(x.data.paginatedBusinessLookups as any))
      );
  }

  fetchQuotaTypesByCode(lookupCode:string,event?: boolean): void {
    if (event) {
      this.pageFilter.search = '';
      this.pageFilter.skip = 0;
    }
    this.quotaTypes$ = this.quotaLookupsByParentCode
      .fetch(
        {
          search: this.pageFilter.search,
          skip: this.pageFilter.skip,
          take: this.pageFilter.take,
          parentCode:lookupCode,
        },
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((x) => new PageResult<BusinessLookup>(x.data.paginatedBusinessLookups as any))
      );
  }

  fetchNextQuotaTypes(event: number): void {
    this.pageFilter.skip = event;
    this.fetchQuotaTypes();
  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchInQuotaTypes(event: any): void {
    this.pageFilter.search = event.term;
    this.pageFilter.skip = null;
    this.fetchQuotaTypes();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addYearlyQuota(model: any) {
      return firstValueFrom(this.addUserYearlyQuota
        .mutate({ model: plainToClass(CreateYearlyQuotaInput, model) },
          {update: (cache) => {ObjectUtils.clearCache(cache,'year');}})
        .pipe(map((x) => x.data.hr.yearlyQuotas)))

  }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateYearlyQuota(model: any) {
    return firstValueFrom(this.updateUserYearlyQuota
      .mutate({ model: plainToClass(UpdateYearlyQuotaInput, model)},
        {update: (cache) => {ObjectUtils.clearCache(cache,'year');}})
      .pipe(map((x) => x.data.hr.yearlyQuotas)))
  }

}
